import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../services/apiClient';

// Helper function to get distinct styles for tags
const getTagDisplayStyles = (tagName) => {
  switch (tagName.toLowerCase()) {
    case 'public':
      return { backgroundColor: '#e0f2f7', color: '#01579b', borderColor: '#01579b' }; // Light blue, dark blue text
    case 'personal':
      return { backgroundColor: '#e8f5e9', color: '#2e7d32', borderColor: '#2e7d32' }; // Light green, dark green text
    default:
      // A generic color for custom tags
      return { backgroundColor: '#f3e5f5', color: '#4a148c', borderColor: '#4a148c' }; // Light purple, dark purple text
  }
};

const MyContacts = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userMobile, setUserMobile] = useState('');
  const [userId, setUserId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isApproved, setIsApproved] = useState(true); // Default to true, set to false only if critical error
  const [approvalMessage, setApprovalMessage] = useState('');

  const [newContact, setNewContact] = useState({
    name: '',
    mobile: '',
    tag: 'public', // Default tag for new contacts
    comment: ''
  });

  const [comments, setComments] = useState({}); // {contactId: [comments]}
  const [updateNotes, setUpdateNotes] = useState({});

  const [availableTags, setAvailableTags] = useState([]); // Will be populated from API
  const [tagMetadata, setTagMetadata] = useState([]); // { id, tagName, isDefault }
  const [newTagName, setNewTagName] = useState(''); // For adding new custom tags
  const [defaultTag, setDefaultTag] = useState('public'); // User's preferred default tag
  const [filterTag, setFilterTag] = useState('all'); // Tag filter for Contact Directory

  // State for real-time validation errors
  const [nameError, setNameError] = useState('');
  const [mobileError, setMobileError] = useState('');
  const [commentError, setCommentError] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // State for responsiveness
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const initializeUser = async () => {
      try {
        const userStr = localStorage.getItem('user');
        if (!userStr) {
          navigate('/login');
          return;
        }

        let user;
        try {
          user = JSON.parse(userStr);
        } catch (e) {
          console.error("Invalid user JSON in localStorage", e);
          localStorage.removeItem('user');
          localStorage.removeItem('userMobile');
          navigate('/login');
          return;
        }

        // Check if token has expired
        if (user && user.exp) {
          const currentTime = Date.now() / 1000;
          if (user.exp < currentTime) {
            console.warn("Session expired. Clearing storage and redirecting to login.");
            localStorage.removeItem('user');
            localStorage.removeItem('userMobile');
            navigate('/login');
            return;
          }
        }

        const mobile = location.state?.mobile || user.phone || '';
        const incomingEmail = location.state?.email || user.email || '';
        setUserEmail(incomingEmail);

        let currentUserId = null;
        let userNeedsApproval = false;
        let approvalReason = "";

        // --- Scenario 1: Mobile number is present in login session ---
        if (mobile) {
          setUserMobile(mobile);
          localStorage.setItem('userMobile', mobile);

          try {
            // 2.1: Call get-gmail-by-mobile API
            const verifyResponse = await apiClient.post('/api/v2/contacts/get-gmail-by-mobile', { mobileNumber: mobile });

            if (verifyResponse.status === 200 && verifyResponse.data) {
              const registeredGmail = verifyResponse.data.toString().toLowerCase().trim();
              const currentGmail = incomingEmail.toLowerCase().trim();

              if (registeredGmail === currentGmail) {
                // Email matches, user is initially considered approved for this step.
                // Now, check/create ContactAppUser for mobile+email combination
                try {
                  let foundUser = null;
                  // NEW ASSUMPTION: Try to fetch ContactAppUser by mobile AND email
                  try {
                    const userByMobileAndEmail = await apiClient.get(`/api/v2/contactappusers/mobile/${mobile}/email/${incomingEmail}`);
                    foundUser = userByMobileAndEmail.data;
                  } catch (err) {
                    if (err.response?.status !== 404) throw err; // Re-throw if not a 404
                  }

                  if (foundUser) {
                    currentUserId = foundUser.id;
                    if (foundUser.defaultTag) {
                      setDefaultTag(foundUser.defaultTag);
                      setNewContact(prev => ({ ...prev, tag: foundUser.defaultTag }));
                    }
                    console.log('ContactAppUser found for mobile+email combination:', currentUserId);
                  } else {
                    // Not found by mobile+email combination, so create
                    const createPayload = { mobile: mobile, email: incomingEmail };
                    const createResponse = await apiClient.post('/api/v2/contactappusers', createPayload);
                    currentUserId = createResponse.data.id;
                    console.log('ContactAppUser created for mobile+email combination:', createResponse.data);
                  }

                } catch (err) {
                  console.error('Error ensuring ContactAppUser record for mobile+email:', err);
                  userNeedsApproval = true;
                  approvalReason = "Error processing user record. Please try again or contact support.";
                }

              } else {
                // Email does NOT match from get-gmail-by-mobile
                userNeedsApproval = true;
                approvalReason = "Approval required: Mobile is registered with a different Gmail account.";
              }
            } else if (verifyResponse.status === 204 || !verifyResponse.data) {
              // get-gmail-by-mobile found no registration
              userNeedsApproval = true;
              approvalReason = "Approval required: No registration found for this mobile.";
            }
          } catch (err) {
            console.error('Error during get-gmail-by-mobile verification:', err);
            userNeedsApproval = true;
            approvalReason = "Error during mobile verification. Please try again.";
          }
        }

        // --- Scenario 2: Mobile number is NOT present, but email is present in login session ---
        else if (incomingEmail) {
          console.log('Mobile number is missing. Validating email against MySQL (Trust app registration)...');

          // Step 2.1: PRIMARY GATE - Check MySQL users table for registered mobile against email
          try {
            const mobileResponse = await apiClient.post('/api/v2/contacts/get-mobile-by-email', { email: incomingEmail });
            
            console.log('Mobile API Response Status:', mobileResponse.status);
            console.log('Mobile API Response Data:', mobileResponse.data);
            console.log('Mobile API Response Data Type:', typeof mobileResponse.data);

            // Extract mobile number - handle both string and number responses
            let registeredMobile = null;
            if (mobileResponse.data) {
              // Convert to string first, then trim
              registeredMobile = String(mobileResponse.data).trim();
            }

            if (registeredMobile && registeredMobile !== '' && registeredMobile !== 'null') {
              console.log('Email validated in MySQL. Mobile found:', registeredMobile);
              setUserMobile(registeredMobile);
              localStorage.setItem('userMobile', registeredMobile);

              // Step 2.2: Find or create ContactAppUser for this mobile+email combination
              try {
                let foundUser = null;
                try {
                  const userByMobileAndEmail = await apiClient.get(`/api/v2/contactappusers/mobile/${registeredMobile}/email/${incomingEmail}`);
                  foundUser = userByMobileAndEmail.data;
                } catch (findErr) {
                  if (findErr.response?.status !== 404) throw findErr;
                }

                if (foundUser) {
                  currentUserId = foundUser.id;
                  if (foundUser.defaultTag) {
                    setDefaultTag(foundUser.defaultTag);
                    setNewContact(prev => ({ ...prev, tag: foundUser.defaultTag }));
                  }
                  console.log('ContactAppUser found for mobile+email:', currentUserId);
                } else {
                  // Create new ContactAppUser with the mobile from MySQL + login email
                  const createPayload = { mobile: registeredMobile, email: incomingEmail };
                  const createResponse = await apiClient.post('/api/v2/contactappusers', createPayload);
                  currentUserId = createResponse.data.id;
                  console.log('ContactAppUser created for mobile+email:', createResponse.data);
                }
              } catch (createErr) {
                console.error('Error ensuring ContactAppUser record:', createErr);
                userNeedsApproval = true;
                approvalReason = "Error processing user record. Please try again or contact support.";
              }
            } else {
              // VALIDATION GATE FAILED - No mobile found in MySQL for this email
              userNeedsApproval = true;
              approvalReason = (
                <span>
                  No user record found for this email in our system. Please, register yourself in{" "}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.trustapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3182ce", textDecoration: "underline" }}
                  >
                    Trust app
                  </a>{" "}
                  and in your profile update this email id then Login again.
                </span>
              );
            }
          } catch (mobileErr) {
            console.error('Error validating email against MySQL:', mobileErr);
            console.error('Error Status:', mobileErr.response?.status);
            console.error('Error Data:', mobileErr.response?.data);
            console.error('Full Error:', mobileErr);
            if (mobileErr.response?.status === 204) {
              // 204 No Content — no mobile registered
              userNeedsApproval = true;
              approvalReason = (
                <span>
                  No user record found for this email in our system. Please, register yourself in{" "}
                  <a
                    href="https://play.google.com/store/apps/details?id=com.trustapp"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#3182ce", textDecoration: "underline" }}
                  >
                    Trust app
                  </a>{" "}
                  and in your profile update this email id then Login again.
                </span>
              );
            } else {
              userNeedsApproval = true;
              approvalReason = `Error validating your registration: ${mobileErr.response?.status || mobileErr.message}. Please try again.`;
            }
          }
        }
        // --- Scenario 3: Neither mobile nor email is present (should not happen after login) ---
        else {
          userNeedsApproval = true;
          approvalReason = "User session is missing both mobile and email. Please log in again.";
        }

        // --- Final check for approval ---
        if (userNeedsApproval) {
          setIsApproved(false);
          setApprovalMessage(approvalReason);
          setLoading(false);
          return;
        }

        // If we reached here, user is approved to view the page
        // Set userId if successfully obtained
        if (currentUserId) {
          setUserId(currentUserId);

          // Fetch available tags for the user
          try {
            const [defaultTagsResponse, userTagsResponse] = await Promise.all([
              apiClient.get('/api/v2/contacts/tags/default'),
              apiClient.get(`/api/v2/contacts/tags/user/${currentUserId}`)
            ]);

            const defaultTags = defaultTagsResponse.data.map(tag => ({
              id: tag.id || null,
              tagName: tag.tagName,
              isDefault: true
            }));
            const userTags = userTagsResponse.data.map(tag => ({
              id: tag.id,
              tagName: tag.tagName,
              isDefault: !!tag.default
            }));

            const allTagMetadata = [...defaultTags, ...userTags];
            const allTagNames = allTagMetadata.map(tag => tag.tagName);
            setTagMetadata(allTagMetadata);
            setAvailableTags([...new Set(allTagNames)]);
          } catch (tagError) {
            console.error('Error fetching tags:', tagError);
            setTagMetadata([]);
            setAvailableTags(['public', 'personal']);
          }

          // Load contacts for this user
          try {
            const contactsResponse = await apiClient.get(`/api/v2/contacts/user/${currentUserId}`);
            setContacts(contactsResponse.data);
            fetchAllComments(contactsResponse.data);
          } catch (err) {
            console.error('Error loading contacts:', err);
            setContacts([]); // Display empty list on error
          }
        } else {
          // This case should ideally not be reached if userNeedsApproval is false,
          // but as a fallback, if no userId was obtained, display default tags and empty contacts.
          console.warn("No userId obtained but user is approved. Displaying default tags and empty contacts.");
          setTagMetadata([]);
          setAvailableTags(['public', 'personal']);
          setContacts([]);
        }

      } catch (error) {
        console.error('Initialization error:', error);
        setIsApproved(false); // Show approval message for any critical initialization error
        setApprovalMessage("An unexpected error occurred during initialization. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    initializeUser();
  }, [navigate, location.state?.mobile, location.state?.email]);

  const fetchAllComments = async (contactList) => {
    contactList.forEach(async (contact) => {
      try {
        const response = await apiClient.get(`/api/v2/contactcomments/contact/${contact.id}`);
        setComments(prev => ({ ...prev, [contact.id]: response.data }));
      } catch (err) {
        console.error(`Error loading comments for contact ${contact.id}:`, err);
      }
    });
  };

  const formatCommentTimestamp = () => {
    const now = new Date();
    return now.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Real-time validation handlers
  const validateName = (name) => {
    if (name.length > 50) {
      setNameError('Contact Name cannot exceed 50 characters.');
    } else {
      setNameError('');
    }
  };

  const validateMobile = (mobile) => {
    const mobileRegex = /^\d{10}$/;
    if (!mobileRegex.test(mobile)) {
      setMobileError('Mobile number must be exactly 10 numeric digits.');
    } else {
      setMobileError('');
    }
  };

  const validateComment = (comment) => {
    if (comment.length > 250) {
      setCommentError('Initial Comment cannot exceed 250 characters.');
    } else {
      setCommentError('');
    }
  };

  const handleAddContact = async (e) => {
    e.preventDefault();

    // Trigger all validations on submit as a final check
    validateName(newContact.name);
    validateMobile(newContact.mobile);
    validateComment(newContact.comment);

    if (!userMobile) {
      alert('User mobile is required');
      return;
    }

    // Prevent submission if any errors exist
    if (nameError || mobileError || commentError) {
      alert('Please correct the input errors before submitting.');
      return;
    }

    try {
      console.log('Creating contact with:', { userMobile, ...newContact });

      const payload = {
        userMobile: userMobile,
        contactMobile: newContact.mobile,
        name: newContact.name,
        tag: newContact.tag || defaultTag || 'public',
        comment: newContact.comment || undefined
      };

      const response = await apiClient.post('/api/v2/contacts', payload);
      console.log('Contact created:', response.data);

      const newContactData = response.data;
      setContacts([newContactData, ...contacts]);

      if (newContact.comment) {
        try {
          const commentsResp = await apiClient.get(`/api/v2/contactcomments/contact/${newContactData.id}`);
          setComments(prev => ({ ...prev, [newContactData.id]: commentsResp.data }));
        } catch (err) {
          console.error('Error loading comments:', err);
        }
      }

      setNewContact({ name: '', mobile: '', tag: defaultTag, comment: '' });
      setNameError(''); // Clear errors on successful submission
      setMobileError('');
      setCommentError('');
      alert('Contact created successfully!');
    } catch (error) {
      console.error('Error creating contact:', error);
      const errorMsg = error.response?.data?.message || error.message;
      alert(`Error creating contact: ${errorMsg}`);
    }
  };

  const handleAddComment = async (contactId, commentText) => {
    if (!commentText || !commentText.trim()) return;

    try {
      const payload = {
        contactId: contactId,
        userId: userId,
        comment: commentText,
        source: 'manual'
      };

      const response = await apiClient.post('/api/v2/contactcomments', payload);
      console.log('Comment created:', response.data);

      setComments(prev => ({
        ...prev,
        [contactId]: [response.data, ...(prev[contactId] || [])]
      }));

      setUpdateNotes({ ...updateNotes, [contactId]: '' });
      alert('Comment added successfully!');
    } catch (error) {
      console.error('Error adding comment:', error.response ? error.response.data : error.message);
      alert('Error adding comment');
    }
  };

  const handleChangeTag = async (contactId, newTag) => {
    try {
      const response = await apiClient.put(
        `/api/v2/contacts/${contactId}/tag`,
        newTag, // Send newTag as a plain string
        { headers: { 'Content-Type': 'text/plain' } } // Explicitly set Content-Type
      );
      console.log('Contact updated:', response.data);

      setContacts(contacts.map(c => c.id === contactId ? response.data : c));
      alert(`Contact tag changed to ${newTag}`);
    } catch (error) {
      console.error('Error updating tag:', error.response ? error.response.data : error.message);
      alert(`Error updating tag: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleAddTag = async (e) => {
    e.preventDefault();
    if (!newTagName.trim()) {
      alert('Tag name cannot be empty.');
      return;
    }
    const normalizedNewTagName = newTagName.trim().toLowerCase();

    if (normalizedNewTagName.length > 10) {
      alert('Tag name cannot exceed 10 characters.');
      return;
    }

    if (availableTags.includes(normalizedNewTagName)) {
      alert('Tag already exists.');
      return;
    }
    // Assuming 'public' and 'personal' are always available and not counted towards the 10 custom tags limit
    const customTagsCount = availableTags.filter(tag => tag !== 'public' && tag !== 'personal').length;
    if (customTagsCount >= 10) {
      alert('Maximum 10 custom tags allowed.');
      return;
    }

    try {
      const payload = { userId: userId, tagName: normalizedNewTagName };
      const response = await apiClient.post('/api/v2/contacts/tags', payload);
      console.log('Tag created:', response.data);
      const createdTag = response.data || {};
      setTagMetadata(prev => [...prev, {
        id: createdTag.id || null,
        tagName: createdTag.tagName || normalizedNewTagName,
        isDefault: false
      }]);
      setAvailableTags(prev => [...prev, createdTag.tagName || normalizedNewTagName]);
      setNewTagName('');
      alert('Tag added successfully!');
    } catch (error) {
      console.error('Error adding tag:', error.response ? error.response.data : error.message);
      alert(`Error adding tag: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteTag = async (tagId, tagName) => {
    if (!tagId) {
      alert('Only custom tags can be deleted.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete the tag "${tagName}"?`)) return;

    try {
      await apiClient.delete(`/api/v2/contacts/tags/${tagId}`);
      setTagMetadata(prev => prev.filter(tag => tag.id !== tagId));
      setAvailableTags(prev => prev.filter(tag => tag !== tagName));
      if (defaultTag === tagName) {
        setDefaultTag('public');
        setNewContact(prev => ({ ...prev, tag: 'public' }));
      }
      if (filterTag === tagName) {
        setFilterTag('all');
      }
      if (newContact.tag === tagName) {
        setNewContact(prev => ({ ...prev, tag: 'public' }));
      }
      alert(`Tag "${tagName}" deleted successfully.`);
    } catch (error) {
      console.error('Error deleting tag:', error.response ? error.response.data : error.message);
      alert(`Error deleting tag: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteContact = async (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    const contactLabel = contact ? `"${contact.name || contact.mobile}"` : 'this contact';

    if (!window.confirm(`Are you sure you want to delete ${contactLabel}?`)) return;
    if (!window.confirm(`⚠️ FINAL CONFIRMATION: This action cannot be undone. Delete ${contactLabel} permanently?`)) return;

    try {
      await apiClient.delete(`/api/v2/contacts/${contactId}`);

      setContacts(contacts.filter(c => c.id !== contactId));
      setComments(prev => {
        const newComments = { ...prev };
        delete newComments[contactId];
        return newComments;
      });

      alert('Contact deleted successfully!');
    } catch (error) {
      console.error('Error deleting contact:', error);
      alert('Error deleting contact');
    }
  };

  const handleDeleteComment = async (commentId, contactId) => {
    if (!window.confirm('Delete this comment?')) return;

    try {
      await apiClient.delete(`/api/v2/contactcomments/${commentId}`);

      setComments(prev => ({
        ...prev,
        [contactId]: prev[contactId].filter(c => c.id !== commentId)
      }));

      alert('Comment deleted!');
    } catch (error) {
      console.error('Error deleting comment:', error);
      alert('Error deleting comment');
    }
  };

  const handleDownloadHTML = () => {
    const allRows = contacts.map(c => `
      <tr>
        <td style="padding:10px; border:1px solid #ddd;">${c.name || ''}</td>
        <td style="padding:10px; border:1px solid #ddd;">${c.mobile}</td>
        <td style="padding:10px; border:1px solid #ddd;">${c.tag}</td>
        <td style="padding:10px; border:1px solid #ddd; white-space:pre-wrap;">${(comments[c.id] || []).map(cm => `${cm.comment} (${new Date(cm.createdAt).toLocaleString('en-IN')})`).join('\n')}</td>
      </tr>`).join('');

    const htmlString = `<html><body style="font-family:sans-serif;"><h2>Contact Export</h2><table style="width:100%; border-collapse:collapse;">
      <tr style="background:#f4f4f4;"><th>Name</th><th>Mobile</th><th>Tag</th><th>Comments</th></tr>
      ${allRows}</table></body></html>`;

    const blob = new Blob([htmlString], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `contacts_export_${new Date().toLocaleDateString()}.html`;
    link.click();
  };

  const handleSignOut = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("userMobile");
    navigate('/login');
  };

  const filteredContacts = filterTag === 'all' ? contacts : contacts.filter(c => c.tag === filterTag);
  const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentContacts = filteredContacts.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}><h2>Loading Directory...</h2></div>;

  if (!isApproved) {
    return (
      <div style={{
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
        fontFamily: 'Inter, sans-serif'
      }}>
        <div style={{ background: "white", padding: "40px", borderRadius: "12px", boxShadow: "0 4px 15px rgba(0,0,0,0.1)", textAlign: "center", maxWidth: "500px" }}>
          <div style={{ fontSize: "4rem", marginBottom: "20px" }}>⏳</div>
          <h2 style={{ color: "#2d3748", marginBottom: "16px" }}>Approval Required</h2>
          <div style={{ marginBottom: "20px", padding: "12px", backgroundColor: "#ebf8ff", borderRadius: "8px", fontSize: "0.875rem", color: "#2c5282", border: "1px solid #bee3f8" }}>
            <div style={{ fontWeight: "700", marginBottom: "4px", textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>Current Session</div>
            <div>{userEmail}</div>
            <div style={{ fontWeight: "600" }}>{userMobile}</div>
          </div>
          <p style={{ color: "#4a5568", lineHeight: "1.6", marginBottom: "24px" }}>{approvalMessage}</p>
          <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={handleSignOut} style={{ padding: "12px 24px", background: "#3182ce", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer", flex: "1 1 auto" }}>Back to Login</button>
            <a
              href="https://www.youtube.com/@TrustApp"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: "12px 24px",
                background: "#FF0000",
                color: "white",
                textDecoration: "none",
                borderRadius: "8px",
                fontWeight: "600",
                cursor: "pointer",
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              Visit YouTube
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", paddingBottom: "40px", fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Enterprise Header */}
      <div style={{
        backgroundColor: "#1a365d",
        padding: isMobile ? "12px 16px" : "16px 24px",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        justifyContent: isMobile ? "center" : "space-between",
        alignItems: "center",
        color: "white",
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
      }}>
        <div style={{ textAlign: isMobile ? "center" : "left", marginBottom: isMobile ? "10px" : "0" }}>
          <h3 style={{ margin: 0, fontWeight: "600", fontSize: isMobile ? "1rem" : "1.25rem", letterSpacing: "-0.025em" }}>Contact Management System</h3>
          <p style={{ margin: "2px 0 0 0", fontSize: isMobile ? "0.75rem" : "0.875rem", opacity: 0.9 }}>User: {userMobile} | Total Contacts: {contacts.length}</p>
        </div>
        <div style={{
          flex: isMobile ? "none" : "1",
          textAlign: "center",
          color: "yellow",
          fontSize: isMobile ? "0.75rem" : "0.875rem",
          padding: isMobile ? "5px 0" : "0 10px",
          marginBottom: isMobile ? "10px" : "0"
        }}>
          Your data is end-to-end encrypted, and nobody, not even our own system, can read it. You can always feel safe with us.
        </div>
        <div style={{ display: "flex", gap: isMobile ? "8px" : "12px", alignItems: "center", flexDirection: isMobile ? "column" : "row" }}>
          <button onClick={() => navigate('/utility-dashboard')} style={{
            padding: isMobile ? "6px 12px" : "8px 16px",
            borderRadius: "6px",
            border: "none",
            background: "#2b6cb0",
            color: "white",
            cursor: "pointer",
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            fontWeight: "500",
            transition: "all 0.2s",
            width: isMobile ? "100%" : "auto"
          }}>🏠 TrustApp Home</button>
          <button onClick={handleDownloadHTML} style={{
            padding: isMobile ? "6px 12px" : "8px 16px",
            borderRadius: "6px",
            border: "none",
            background: "#4a5568",
            color: "white",
            cursor: "pointer",
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            fontWeight: "500",
            transition: "all 0.2s",
            width: isMobile ? "100%" : "auto"
          }}>Export Data</button>
          <button onClick={handleSignOut} style={{
            padding: isMobile ? "6px 12px" : "8px 16px",
            borderRadius: "6px",
            border: "none",
            background: "#e53e3e",
            color: "white",
            cursor: "pointer",
            fontSize: isMobile ? "0.75rem" : "0.875rem",
            fontWeight: "500",
            transition: "all 0.2s",
            width: isMobile ? "100%" : "auto"
          }}>Sign Out</button>
        </div>
      </div>

      <div style={{ maxWidth: "1400px", margin: "24px auto", padding: "0 24px" }}>

        {/* Tag Management Section */}
        <div style={{ background: "#dbeafe", padding: "24px", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.05)", marginBottom: "24px", border: "1px solid #93c5fd" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
            <div style={{ width: "4px", height: "24px", backgroundColor: "#3182ce", borderRadius: "2px", marginRight: "12px" }}></div>
            <h4 style={{ margin: 0, color: "#2d3748", fontSize: "1.125rem", fontWeight: "600" }}>Manage Tags</h4>
          </div>
          <div style={{ marginBottom: "16px" }}>
            <p style={{ margin: "0 0 8px 0", fontSize: "0.875rem", color: "#4a5568" }}>Available Tags: <span style={{ fontSize: "0.75rem", color: "#718096" }}>(⭐ = default for new contacts)</span></p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {availableTags.map(tag => {
                const tagInfo = tagMetadata.find(item => item.tagName === tag);
                const isDefault = tag === defaultTag;
                const isCustomTag = !!tagInfo && !tagInfo.isDefault && tagInfo.id;
                return (
                  <span
                    key={tag}
                    style={{
                      ...getTagDisplayStyles(tag),
                      padding: "6px 12px",
                      borderRadius: "9999px",
                      fontSize: "0.75rem",
                      fontWeight: "500",
                      border: isDefault ? "2px solid" : "1px solid",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      boxShadow: isDefault ? "0 0 8px rgba(49, 130, 206, 0.3)" : "none"
                    }}
                  >
                    {tag}
                    {isCustomTag && (
                      <button
                        onClick={() => handleDeleteTag(tagInfo.id, tag)}
                        title={`Delete "${tag}"`}
                        style={{
                          background: "transparent",
                          border: "none",
                          cursor: "pointer",
                          color: "#b91c1c",
                          fontSize: "0.875rem",
                          lineHeight: "1",
                          padding: "0",
                          opacity: 0.8
                        }}
                      >
                        ×
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (isDefault) return; // Already default
                        try {
                          await apiClient.put(
                            `/api/v2/contactappusers/${userId}/default-tag`,
                            tag,
                            { headers: { 'Content-Type': 'text/plain' } }
                          );
                          setDefaultTag(tag);
                          setNewContact(prev => ({ ...prev, tag: tag }));
                          alert(`"${tag}" is now your default tag for new contacts.`);
                        } catch (error) {
                          console.error('Error setting default tag:', error);
                          alert('Error setting default tag. Please try again.');
                        }
                      }}
                      title={isDefault ? 'Current default tag' : `Set "${tag}" as default`}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: isDefault ? "default" : "pointer",
                        padding: "0",
                        fontSize: "0.875rem",
                        lineHeight: "1",
                        opacity: isDefault ? 1 : 0.5,
                        transition: "opacity 0.2s"
                      }}
                      onMouseEnter={(e) => { if (!isDefault) e.target.style.opacity = '1'; }}
                      onMouseLeave={(e) => { if (!isDefault) e.target.style.opacity = '0.5'; }}
                    >
                      {isDefault ? '⭐' : '☆'}
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
          <form onSubmit={handleAddTag} style={{ display: "flex", gap: "12px" }}>
            <input
              type="text"
              placeholder="New Tag Name (e.g., 'Work', 'Family')"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              maxLength={10} // Added maxLength attribute
              style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "0.875rem", backgroundColor: "#f8f9fa", boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)" }}
            />
            <button type="submit" style={{ padding: "12px 24px", background: "#3182ce", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "0.875rem", transition: "all 0.2s" }}>
              Add Tag
            </button>
          </form>
        </div>

        {/* Add New Contact Section */}
        <div style={{ background: "#fef3c7", padding: "24px", borderRadius: "12px", boxShadow: "0 4px 6px rgba(0,0,0,0.05)", marginBottom: "24px", border: "1px solid #fcd34d" }}>
          <div style={{ display: "flex", alignItems: "center", marginBottom: "20px" }}>
            <div style={{ width: "4px", height: "24px", backgroundColor: "#3182ce", borderRadius: "2px", marginRight: "12px" }}></div>
            <h4 style={{ margin: 0, color: "#2d3748", fontSize: "1.125rem", fontWeight: "600" }}>Add New Contact</h4>
          </div>
          <form onSubmit={handleAddContact} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "16px" }}>
            <div style={{ position: 'relative', width: '100%' }}> {/* Wrapper for input and error */}
              <input
                type="text" // Ensure text type for name
                placeholder="Contact Name"
                required
                value={newContact.name}
                onChange={e => {
                  setNewContact({...newContact, name: e.target.value});
                  validateName(e.target.value); // Real-time validation
                }}
                onBlur={e => validateName(e.target.value)} // Validate on blur too
                maxLength={50} // Enforce max length
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  border: nameError ? "1px solid red" : "1px solid #e2e8f0", // Red border on error
                  fontSize: "0.875rem",
                  backgroundColor: "#f8f9fa",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                  width: '100%'
                }}
              />
              {nameError && <p style={{ color: 'red', fontSize: '0.75rem', margin: '4px 0 0 0', position: 'absolute', bottom: '-20px', left: '0' }}>{nameError}</p>}
            </div>

            <div style={{ position: 'relative', width: '100%' }}> {/* Wrapper for input and error */}
              <input
                type="tel" // Use tel type for mobile numbers
                placeholder="Contact Mobile"
                required
                value={newContact.mobile}
                onChange={e => {
                  const numericValue = e.target.value.replace(/\D/g, ''); // Allow only digits
                  setNewContact({...newContact, mobile: numericValue});
                  validateMobile(numericValue); // Real-time validation
                }}
                onBlur={e => validateMobile(e.target.value)} // Validate on blur too
                maxLength={10} // Enforce max length
                style={{
                  padding: "12px",
                  borderRadius: "8px",
                  border: mobileError ? "1px solid red" : "1px solid #e2e8f0", // Red border on error
                  fontSize: "0.875rem",
                  backgroundColor: "#f8f9fa",
                  boxShadow: "inset 0 1px 2px rgba(0,0,0,0.05)",
                  width: '100%'
                }}
              />
              {mobileError && <p style={{ color: 'red', fontSize: '0.75rem', margin: '4px 0 0 0', position: 'absolute', bottom: '-20px', left: '0' }}>{mobileError}</p>}
            </div>

            {/* Tag Dropdown for New Contact */}
            <select
              value={newContact.tag}
              onChange={e => setNewContact({...newContact, tag: e.target.value})}
              style={{ padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "0.875rem", backgroundColor: "#f8f9fa" }}
            >
              {availableTags.map(tag => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>

            <div style={{ position: 'relative', gridColumn: "1 / -1", width: '100%' }}> {/* Wrapper for textarea and error */}
              <textarea
                placeholder="Initial comment..."
                value={newContact.comment}
                onChange={e => {
                  setNewContact({...newContact, comment: e.target.value});
                  validateComment(e.target.value); // Real-time validation
                }}
                onBlur={e => validateComment(e.target.value)} // Validate on blur too
                maxLength={250} // Enforce max length
                style={{
                  gridColumn: "1 / -1",
                  padding: "12px",
                  borderRadius: "8px",
                  border: commentError ? "1px solid red" : "1px solid #e2e8f0", // Red border on error
                  minHeight: "80px",
                  fontSize: "0.875rem",
                  backgroundColor: "#f8f9fa",
                  resize: "vertical",
                  width: '100%'
                }}
              />
              {commentError && <p style={{ color: 'red', fontSize: '0.75rem', margin: '4px 0 0 0', position: 'absolute', bottom: '-20px', left: '0' }}>{commentError}</p>}
            </div>

            <button type="submit" style={{ gridColumn: "1 / -1", padding: "12px 24px", background: "linear-gradient(135deg, #48bb78 0%, #38a169 100%)", color: "white", border: "none", borderRadius: "8px", fontWeight: "600", cursor: "pointer", fontSize: "0.875rem", transition: "all 0.2s", boxShadow: "0 2px 4px rgba(72, 187, 120, 0.2)" }}>Save Contact</button>
          </form>
        </div>

        {/* Contacts Table */}
        <div style={{
          background: "white",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
          overflowX: 'auto'
        }}>
          <div style={{
            padding: "20px 24px",
            borderBottom: "1px solid #e2e8f0",
            backgroundColor: "#e0f7fa", // Light blue background
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
              <h4 style={{ margin: 0, color: "#00796b", fontSize: "1.125rem", fontWeight: "700" }}>Contact Directory</h4>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label htmlFor="tagFilter" style={{ fontSize: "0.8rem", color: "#00796b", fontWeight: "600", whiteSpace: "nowrap" }}>Filter by Tag:</label>
                <select
                  id="tagFilter"
                  value={filterTag}
                  onChange={(e) => { setFilterTag(e.target.value); setCurrentPage(1); }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "1px solid #b2dfdb",
                    fontSize: "0.8rem",
                    backgroundColor: "white",
                    color: "#00796b",
                    fontWeight: "500",
                    cursor: "pointer",
                    outline: "none"
                  }}
                >
                  <option value="all">All Tags ({contacts.length})</option>
                  {availableTags.map(tag => {
                    const count = contacts.filter(c => c.tag === tag).length;
                    return <option key={tag} value={tag}>{tag} ({count})</option>;
                  })}
                </select>
              </div>
            </div>
            <p style={{ margin: 0, fontSize: "0.875rem", color: "#00796b" }}>
              {filteredContacts.length === 0 ? 'No contacts found' : `Showing ${startIndex + 1}-${Math.min(endIndex, filteredContacts.length)} of ${filteredContacts.length} contacts`}
            </p>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ background: "#00796b", borderBottom: "2px solid #004d40", color: "white" }}> {/* Dark green header */}
                <th style={{ padding: "16px 20px", textAlign: "left", fontWeight: "600", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Contact Info</th>
                <th style={{ padding: "16px 20px", textAlign: "left", fontWeight: "600", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Tag</th>
                <th style={{ padding: "16px 20px", textAlign: "left", fontWeight: "600", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Comments</th>
                <th style={{ padding: "16px 20px", textAlign: "center", fontWeight: "600", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentContacts.map((contact, index) => (
                <tr
                  key={contact.id}
                  style={{
                    borderBottom: "1px solid #e0f2f7", // Lighter border
                    background: index % 2 === 0 ? "#f5fafd" : "white", // Zebra striping
                    transition: "background-color 0.2s ease-in-out"
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e0f2f7'} // Hover effect
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#f5fafd" : "white"}
                >
                  <td style={{ padding: "20px", verticalAlign: "top", width: "25%" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      <div style={{ width: "40px", height: "40px", borderRadius: "50%", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: "600", flexShrink: 0 }}>
                        {(contact.name || contact.mobile).charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5 style={{ margin: "0 0 4px 0", color: "#2d3748", fontSize: "1rem", fontWeight: "600" }}>{contact.name || 'N/A'}</h5>
                        <p style={{ margin: 0, color: "#3182ce", fontSize: "0.875rem", fontWeight: "500" }}>{contact.mobile}</p>
                        {/* WhatsApp and Caller Icons */}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                          <a
                            href={`tel:${contact.mobile}`} // Corrected: Caller action
                            title={`Call ${contact.mobile}`} // Corrected: Caller label
                            style={{
                              fontSize: '1.2rem', // Adjusted size
                              color: '#007bff', // Blue for call
                              textDecoration: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            📞 {/* Phone Icon */}
                          </a>
                          <a
                            href={`https://wa.me/${contact.mobile}`} // Corrected: WhatsApp action
                            title={`WhatsApp ${contact.mobile}`} // Corrected: WhatsApp label
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '1.2rem',
                              color: '#25D366', // Green for WhatsApp
                              textDecoration: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <img src={require('../assets/images/whatsapp.png')} alt="WhatsApp" style={{ width: '1.2rem', height: '1.2rem' }} /> {/* WhatsApp Image Icon */}
                          </a>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "20px", textAlign: "center", verticalAlign: "top" }}>
                    <select
                      value={contact.tag}
                      onChange={(e) => handleChangeTag(contact.id, e.target.value)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: "6px",
                        border: `1px solid #a7d9e8`, // Lighter border for select
                        background: "#e0f2f7", // Light blue background
                        color: "#01579b", // Dark blue text
                        fontWeight: "600",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        textTransform: "capitalize"
                      }}
                    >
                      {availableTags.map(tag => (
                        <option key={tag} value={tag}>{tag}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: "20px", verticalAlign: "top", width: "35%" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                      {/* Comments Display */}
                      <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                        {(comments[contact.id] || []).length > 0 ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            {comments[contact.id].map((comment, idx) => (
                              <div key={idx} style={{ background: "#f1f5f9", padding: "10px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "0.8125rem" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                  <div style={{ flex: 1 }}>
                                    <p style={{ margin: "0 0 4px 0", whiteSpace: "pre-wrap", color: "#2d3748" }}>{comment.comment}</p>
                                    <p style={{ margin: 0, fontSize: "0.75rem", color: "#718096" }}>
                                      {new Date(comment.createdAt).toLocaleString('en-IN')}
                                    </p>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteComment(comment.id, contact.id)}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      color: "#e53e3e",
                                      cursor: "pointer",
                                      fontSize: "0.75rem",
                                      padding: "2px 4px",
                                      marginLeft: "8px"
                                    }}
                                  >
                                    ✕
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ color: "#a0aec0", fontStyle: "italic", fontSize: "0.875rem" }}>No comments yet</div>
                        )}
                      </div>

                      {/* Add Comment Section */}
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <input
                          placeholder="Add comment..."
                          value={updateNotes[contact.id] || ''}
                          onChange={(e) => setUpdateNotes({ ...updateNotes, [contact.id]: e.target.value })}
                          style={{ flex: 1, padding: "8px 12px", borderRadius: "6px", border: "1px solid #e2e8f0", fontSize: "0.8125rem", backgroundColor: "#f8f9fa" }}
                        />
                        <button
                          onClick={() => handleAddComment(contact.id, updateNotes[contact.id])}
                          style={{
                            padding: "8px 12px",
                            background: "#3182ce",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                            whiteSpace: "nowrap"
                          }}
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "20px", textAlign: "center", verticalAlign: "top" }}>
                    <button
                      onClick={() => handleDeleteContact(contact.id)}
                      style={{
                        padding: "8px 12px",
                        background: "#fed7d7",
                        color: "#c53030",
                        border: "1px solid #fc8181",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "0.75rem",
                        fontWeight: "500",
                        transition: "all 0.2s"
                      }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              padding: "20px 24px",
              borderTop: "1px solid #e2e8f0",
              backgroundColor: "#e0f7fa", // Light blue background
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px"
            }}>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #a7d9e8", // Lighter border
                  borderRadius: "6px",
                  background: currentPage === 1 ? "#f5fafd" : "white", // Match zebra striping
                  color: currentPage === 1 ? "#a0aec0" : "#01579b", // Dark blue text
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "0.8125rem",
                  transition: "all 0.2s"
                }}
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #a7d9e8", // Lighter border
                    borderRadius: "6px",
                    background: currentPage === page ? "#00796b" : "white", // Dark green for active, white for others
                    color: currentPage === page ? "white" : "#00796b", // White text for active, dark green for others
                    cursor: "pointer",
                    fontSize: "0.8125rem",
                    fontWeight: currentPage === page ? "600" : "400",
                    transition: "all 0.2s",
                    minWidth: "40px"
                  }}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #a7d9e8", // Lighter border
                  borderRadius: "6px",
                  background: currentPage === totalPages ? "#f5fafd" : "white", // Match zebra striping
                  color: currentPage === totalPages ? "#a0aec0" : "#01579b", // Dark blue text
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "0.8125rem",
                  transition: "all 0.2s"
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {contacts.length === 0 && (
          <div style={{ textAlign: "center", padding: "60px 24px", color: "#a0aec0", background: "white", borderRadius: "12px", border: "1px solid #e2e8f0", marginTop: "24px" }}>
            <div style={{ fontSize: "3rem", marginBottom: "16px" }}>📋</div>
            <h4 style={{ margin: "0 0 8px 0", color: "#4a5568" }}>No contacts found</h4>
            <p style={{ margin: 0, fontSize: "0.875rem" }}>Start building your contact network by adding your first contact above.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyContacts;